import { useMutation, useQueryClient } from '@tanstack/react-query'
import { flattenParts } from '@kronos/domain'
import type { PartsEntry } from '@kronos/domain'
import { repository } from '../../../data'
import type { ComponentType, StructureSaved } from '../../../data'
import type { PreparedImage } from '../../imageFile'
import { NEW_CATEGORY, forUpdate, hasParts, payloadOf } from '../itemDraft'
import type { Draft } from '../itemDraft'

export interface SaveInput {
  readonly draft: Draft
  readonly parts: readonly PartsEntry[]
  readonly partsDirty: boolean
  readonly staged: PreparedImage | null
}

export interface SaveResult {
  readonly id: string
  /** Present when the parts list was saved — `frozen` non-null means it forked. */
  readonly structure: StructureSaved | null
}

/**
 * Save, in the order the rows depend on each other: a new category first (the
 * item names it), then the item, then its photo (it needs the id), then what
 * it is made of (the lines name their parent).
 *
 * Not one transaction, and honest about it: if a later step fails the item
 * exists without its photo or parts list — the state every item is in before
 * somebody fills it in, recoverable by opening it again. The category made in
 * step one is handed back (`onCategory`), so a retry does not trip over it.
 */
export function useItemSave(existing: ComponentType | undefined, onCategory: (name: string) => void) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ draft, parts, partsDirty, staged }: SaveInput): Promise<SaveResult> => {
      let category = draft.category
      if (category === NEW_CATEGORY) {
        category = (await repository.addCategory(draft.newCategory)).name
        onCategory(category)
      }
      const body = payloadOf(draft, category)
      let id: string
      if (existing) {
        await repository.updateComponentType(existing.id, forUpdate(body, existing))
        id = existing.id
      } else {
        id = (await repository.createComponentType(body)).id
      }
      if (staged) {
        await repository.uploadAttachment({ kind: 'component_type', id }, staged.blob, { width: staged.width, height: staged.height })
      }
      // `saveStructure` decides edit-in-place or fork with `mayEditLines`; the
      // `partsDirty` guard is what keeps a rename from forking a revision.
      const structure = hasParts(draft) && partsDirty ? await repository.saveStructure(id, flattenParts(parts)) : null
      return { id, structure }
    },
    onSuccess: () => client.invalidateQueries(),
  })
}
